import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Play, ChevronRight, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import { usePlayer } from "../context/PlayerContext";
import { useSettings } from "../context/SettingsContext";
import { videoSchema, breadcrumbSchema } from "../lib/schema";
import Seo from "../components/Seo";
import { VideoCard } from "../components/VideosSection";
import ShareMenu from "../components/ShareMenu";
import FollowStrip from "../components/FollowStrip";
import DSPButtons from "../components/DSPButtons";
import EmailCapture from "../components/EmailCapture";
import Footer from "../components/Footer";
import NotFound from "./NotFound";
import { Reveal } from "../components/Reveal";

const TYPES = [["all", "All"], ["music_video", "Music Videos"], ["lyric", "Lyric Videos"], ["visualizer", "Visualizers"], ["short", "Shorts"], ["promo", "Promo"], ["live", "Live"]];

export function Videos() {
  const settings = useSettings();
  const [videos, setVideos] = useState([]);
  const [type, setType] = useState("all");
  useEffect(() => { window.scrollTo(0, 0); track("videos_view", {}); api.get("/videos").then((r) => setVideos(r.data || [])).catch(() => {}); }, []);
  const shown = type === "all" ? videos : videos.filter((v) => v.videoType === type);
  const available = TYPES.filter(([k]) => k === "all" || videos.some((v) => v.videoType === k));
  return (
    <div className="grain relative min-h-screen pb-32">
      <Seo title="Videos — Official Music Videos, Visualizers & Lyric Videos" description="Watch DJ Código's official music videos, lyric videos and visualizers. Latin urban, reggaetón and hip-hop from a Dominican independent artist." path="/videos" jsonLd={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Videos", path: "/videos" }], settings)} />
      <div className="px-6 pt-28 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <p className="text-xs font-bold uppercase tracking-[0.35em] text-gold">Watch</p>
          <h1 className="mt-3 font-display text-4xl font-bold uppercase leading-none tracking-tight text-white sm:text-6xl">Videos</h1>
          <p className="mt-3 max-w-xl text-sm text-white/60">Official music videos, visualizers and lyric videos from DJ Código. Press play, then stream the song.</p>
          <div className="mt-8 flex flex-wrap gap-2" data-testid="video-filters">
            {available.map(([k, l]) => (
              <button key={k} data-testid={`video-filter-${k}`} onClick={() => setType(k)} className={`rounded-full border px-4 py-2 text-[11px] font-bold uppercase tracking-widest transition-colors ${type === k ? "border-gold bg-gold text-black" : "border-white/15 text-white/60 hover:text-white"}`}>{l}</button>
            ))}
          </div>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="videos-grid">
            {shown.map((v, i) => <VideoCard key={v.youtubeId} video={v} index={i} autoPlayOnClick={false} />)}
          </div>
          {shown.length === 0 && <p className="mt-8 text-sm text-white/40">No videos yet — subscribe on YouTube to catch the next one.</p>}
          <FollowStrip settings={settings} placement="videos" className="mt-12" />
        </div>
      </div>
      <EmailCapture source="videos" />
      <Footer settings={settings} />
    </div>
  );
}

export function VideoDetail() {
  const { slug } = useParams();
  const settings = useSettings();
  const { playRelease } = usePlayer();
  const [video, setVideo] = useState(null);
  const [more, setMore] = useState([]);
  const [status, setStatus] = useState("loading");
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPlaying(false);
    api.get(`/videos/${slug}`).then((r) => { setVideo(r.data); setStatus("ok"); track("video_view", { releaseId: r.data.releaseId, metadata: { slug } }); }).catch(() => setStatus("404"));
    api.get("/videos").then((r) => setMore((r.data || []).filter((v) => v.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  if (status === "404") return <NotFound />;
  if (!video) return <div className="min-h-screen bg-ink" />;
  const rel = video.release;
  const play = () => { track("video_play", { releaseId: video.releaseId, metadata: { youtubeId: video.youtubeId, slug, placement: "video_page" } }); setPlaying(true); };

  return (
    <div className="grain relative min-h-screen pb-32">
      <Seo
        title={`${video.title} (Official Video)`}
        description={video.description?.slice(0, 160) || `Watch ${video.title} by DJ Código — official ${video.videoType?.replace("_", " ")}. Then stream the song on Spotify and Apple Music.`}
        image={video.thumbnail}
        type="video.other"
        path={`/videos/${video.slug}`}
        jsonLd={[videoSchema(video, settings), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Videos", path: "/videos" }, { name: video.title, path: `/videos/${video.slug}` }], settings)]}
      />
      <div className="px-6 pt-24 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-5xl">
          <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-white/50">
            <Link to="/" className="inline-flex items-center gap-1.5 hover:text-gold"><ArrowLeft size={14} /> Home</Link><ChevronRight size={12} /><Link to="/videos" className="hover:text-gold">Videos</Link><ChevronRight size={12} /><span className="truncate text-white/80">{video.title}</span>
          </nav>
          <div className="relative aspect-video overflow-hidden rounded-3xl border border-white/10 bg-black" data-testid="video-player">
            {playing ? (
              <iframe title={video.title} className="h-full w-full" src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?autoplay=1&rel=0`} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
            ) : (
              <button data-testid="video-play" onClick={play} aria-label={`Play ${video.title}`} className="group relative block h-full w-full">
                <img src={`https://i.ytimg.com/vi/${video.youtubeId}/maxresdefault.jpg`} onError={(e) => { e.currentTarget.src = video.thumbnail; }} alt={`${video.title} — DJ Código`} className="h-full w-full object-cover" fetchPriority="high" />
                <span className="absolute inset-0 bg-ink/30" />
                <span className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-gold text-black transition-transform group-hover:scale-110"><Play size={32} fill="currentColor" className="ml-1" /></span>
              </button>
            )}
          </div>
          <div className="mt-8 grid gap-8 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-gold">{video.videoType?.replace("_", " ")} • {video.publishedAt?.slice(0, 10)}</p>
              <h1 className="mt-2 font-display text-3xl font-bold uppercase leading-tight tracking-tight text-white sm:text-5xl">{video.title}</h1>
              <p className="mt-2 text-lg text-gold">DJ Código</p>
              {video.description && <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-white/60">{video.description.slice(0, 600)}</p>}
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <ShareMenu kind="v" slug={video.slug} title={video.title} text={`Watch ${video.title} — DJ Código`} releaseId={video.releaseId} eventType="share_video" label="Share Video" />
                <a href={`https://www.youtube.com/watch?v=${video.youtubeId}`} target="_blank" rel="noreferrer" onClick={() => track("youtube_click", { releaseId: video.releaseId, metadata: { placement: "video_page" } })} className="text-xs font-semibold uppercase tracking-widest text-white/50 hover:text-gold">Watch on YouTube →</a>
              </div>
              <FollowStrip settings={settings} placement="video" className="mt-8" />
            </div>
            {rel && (
              <Reveal>
                <div data-testid="video-release" className="rounded-3xl border border-gold/30 bg-surface/60 p-5">
                  <p className="mb-3 text-[11px] font-bold uppercase tracking-widest text-gold">Stream the song</p>
                  <div className="flex items-center gap-4">
                    <img src={rel.artwork} alt={`${rel.title} cover`} className="h-20 w-20 rounded-xl object-cover" />
                    <div className="min-w-0">
                      <Link to={`/music/${rel.slug}`} className="font-display text-xl font-semibold text-white hover:text-gold">{rel.title}</Link>
                      <p className="text-xs uppercase tracking-widest text-white/40">{rel.releaseType} • {rel.releaseDate?.slice(0, 4)}</p>
                    </div>
                  </div>
                  <button data-testid="video-stream-song" onClick={() => { track("campaign_cta_click", { releaseId: rel.internalId, metadata: { placement: "video_page", action: "listen" } }); playRelease(rel); }} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-black"><Play size={16} fill="currentColor" /> Listen Now</button>
                  <div className="mt-3"><DSPButtons release={rel} size="sm" /></div>
                </div>
              </Reveal>
            )}
          </div>
        </div>
      </div>
      {more.length > 0 && (
        <section className="px-6 pt-16 sm:px-10 lg:px-16" data-testid="watch-more">
          <div className="mx-auto max-w-5xl">
            <Reveal><p className="mb-2 text-xs font-bold uppercase tracking-[0.3em] text-gold">Watch more</p><h2 className="mb-6 font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-4xl">More videos</h2></Reveal>
            <div className="grid gap-5 sm:grid-cols-3">{more.map((v, i) => <VideoCard key={v.youtubeId} video={v} index={i} autoPlayOnClick={false} />)}</div>
          </div>
        </section>
      )}
      <EmailCapture source={`video:${video.slug}`} releaseId={video.releaseId} />
      <Footer settings={settings} />
    </div>
  );
}
